// 性能监控工具

export class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.observers = {};
    this.init();
  }

  init() {
    // 监控页面加载性能
    this.observePageLoad();
    
    // 监控长任务
    this.observeLongTasks();
    
    // 监控布局偏移
    this.observeLayoutShift();
    
    // 监控首次内容绘制
    this.observePaint();
  }

  // 页面加载性能
  observePageLoad() {
    if ('performance' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const navigation = performance.getEntriesByType('navigation')[0];
          if (navigation) {
            this.metrics.pageLoad = {
              domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
              loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
              totalTime: navigation.loadEventEnd - navigation.fetchStart
            };
          }
        }, 0);
      });
    }
  }

  // 监控长任务（>50ms）
  observeLongTasks() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (entry.duration > 50) {
              console.warn(`长任务检测: ${entry.duration}ms`, entry);
            }
          });
        });
        observer.observe({ entryTypes: ['longtask'] });
        this.observers.longTask = observer;
      } catch (e) {
        console.log('Long task monitoring not supported');
      }
    }
  }

  // 监控累积布局偏移
  observeLayoutShift() {
    if ('PerformanceObserver' in window) {
      try {
        let clsValue = 0;
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.metrics.cls = clsValue;
        });
        observer.observe({ entryTypes: ['layout-shift'] });
        this.observers.layoutShift = observer;
      } catch (e) {
        console.log('Layout shift monitoring not supported');
      }
    }
  }

  // 监控绘制性能
  observePaint() {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            this.metrics[entry.name] = entry.startTime;
          });
        });
        observer.observe({ entryTypes: ['paint'] });
        this.observers.paint = observer;
      } catch (e) {
        console.log('Paint monitoring not supported');
      }
    }
  }

  // 测量函数执行时间
  measureFunction(name, fn) {
    const start = performance.now();
    const result = fn();
    const end = performance.now();
    
    if (!this.metrics.functions) {
      this.metrics.functions = {};
    }
    
    this.metrics.functions[name] = end - start;
    
    if (end - start > 16) { // 超过一帧的时间
      console.warn(`函数 ${name} 执行时间过长: ${end - start}ms`);
    }
    
    return result;
  }

  // 测量异步函数执行时间
  async measureAsyncFunction(name, fn) {
    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    
    if (!this.metrics.functions) {
      this.metrics.functions = {};
    }
    
    this.metrics.functions[name] = end - start;
    return result;
  }

  // 获取内存使用情况
  getMemoryUsage() {
    if ('memory' in performance) {
      return {
        used: performance.memory.usedJSHeapSize,
        total: performance.memory.totalJSHeapSize,
        limit: performance.memory.jsHeapSizeLimit
      };
    }
    return null;
  }

  // 获取网络连接信息
  getConnectionInfo() {
    if ('connection' in navigator) {
      return {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      };
    }
    return null;
  }

  // 获取所有性能指标
  getMetrics() {
    return {
      ...this.metrics,
      memory: this.getMemoryUsage(),
      connection: this.getConnectionInfo(),
      timestamp: Date.now()
    };
  }

  // 清理观察器
  cleanup() {
    Object.values(this.observers).forEach(observer => {
      if (observer && observer.disconnect) {
        observer.disconnect();
      }
    });
  }
}

// 图片懒加载工具
export class LazyLoader {
  constructor(options = {}) {
    this.options = {
      rootMargin: '50px',
      threshold: 0.1,
      ...options
    };
    this.observer = null;
    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        this.options
      );
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        const src = img.dataset.src;
        
        if (src) {
          img.src = src;
          img.classList.add('loaded');
          img.removeAttribute('data-src');
          this.observer.unobserve(img);
        }
      }
    });
  }

  observe(element) {
    if (this.observer) {
      this.observer.observe(element);
    } else {
      // 降级处理
      const src = element.dataset.src;
      if (src) {
        element.src = src;
        element.classList.add('loaded');
        element.removeAttribute('data-src');
      }
    }
  }

  observeAll(selector = '[data-src]') {
    const images = document.querySelectorAll(selector);
    images.forEach(img => this.observe(img));
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// 防抖函数
export function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(this, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(this, args);
  };
}

// 节流函数
export function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// 检测设备性能
export function getDevicePerformance() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const memory = navigator.deviceMemory;
  const cores = navigator.hardwareConcurrency;

  let performanceLevel = 'high';

  if (connection) {
    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      performanceLevel = 'low';
    } else if (connection.effectiveType === '3g') {
      performanceLevel = 'medium';
    }
  }

  if (memory && memory <= 2) {
    performanceLevel = 'low';
  } else if (memory && memory <= 4) {
    performanceLevel = 'medium';
  }

  if (cores && cores <= 2) {
    performanceLevel = 'low';
  }

  return {
    level: performanceLevel,
    connection: connection ? connection.effectiveType : 'unknown',
    memory: memory || 'unknown',
    cores: cores || 'unknown'
  };
}

// 创建全局实例
export const performanceMonitor = new PerformanceMonitor();
export const lazyLoader = new LazyLoader();

// 在页面卸载时清理
window.addEventListener('beforeunload', () => {
  performanceMonitor.cleanup();
  lazyLoader.disconnect();
});
